<template>

  <div>
    <b-row
      class="content-header"
    >

      <!-- Content Left -->
      <b-col
        class="content-header-left mb-2"
        cols="7"
        md="9"
      >
        <b-row class="breadcrumbs-top">
          <b-col cols="12">
            <h2 class="custom-header-title float-left pr-1 mb-0">
              Expense Categories
            </h2>
          </b-col>
        </b-row>
      </b-col>

      <!-- Content Right -->
      <b-col
        class="content-header-right text-right mb-1"
        md="3"
        cols="5"
      >
        <b-button
          v-if="canViewThisAction('create', 'ExpenseCategory')"
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          variant="success"
          class="mobile_create_button"
          @click="openCreateExpenseCategoryModal()"
        >
          <feather-icon
            icon="PlusIcon"
            class="mr-50"
            size="16"
          />
          <span class="align-middle d-md-block d-none">Create New Expense Category</span>
          <span class="align-middle d-md-none d-block">New</span>
        </b-button>
      </b-col>
    </b-row>
    <!-- Table Container Card -->
    <b-card
      no-body
      class="mb-0"
    >

      <div class="m-2">

        <!-- Table Top -->
        <b-row>
          <b-col
            v-if="isMobile()"
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-space_between mb-1 mb-md-0"
          >
            <div class="bulk_selection d-flex">
              <b-dropdown
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                class="mx-1"
                right
                :text="$route.query.categoryType ? $route.query.categoryType : selectedExpenseCategoryType"
                variant="primary"
              >
                <b-dropdown-item
                  v-for="(eCategoryType, index) in expenseCategoryTypeOptions"
                  :key="index"
                  @click="expenseCategoryTypeFiltering(eCategoryType)"
                >
                  {{ eCategoryType }}
                </b-dropdown-item>
              </b-dropdown>
            </div>
            <span class="text-muted">Showing <br>{{ dataMeta.from }} - {{ dataMeta.to }} of {{ dataMeta.of }}</span>
          </b-col>
          <!-- Per Page -->
          <b-col
            v-else
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <b-dropdown
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              class="mx-1"
              right
              :text="$route.query.categoryType ? $route.query.categoryType : selectedExpenseCategoryType"
              variant="outline-primary"
            >
              <b-dropdown-item
                v-for="(eCategoryType, index) in expenseCategoryTypeOptions"
                :key="index"
                @click="expenseCategoryTypeFiltering(eCategoryType)"
              >
                {{ eCategoryType }}
              </b-dropdown-item>
            </b-dropdown>
            <span class="text-muted">Showing {{ dataMeta.from }} - {{ dataMeta.to }} of {{ dataMeta.of }}</span>
          </b-col>

          <!-- Search -->
          <b-col
            cols="12"
            md="6"
          >
            <div class="d-flex align-items-center justify-content-end">
              <a
                href="javascript:void(0)"
                class="nav-link nav-link-search"
                @click="showSearchBar = !showSearchBar"
              >
                <feather-icon
                  icon="SearchIcon"
                  size="21"
                  class="mr-50"
                />
                <span class="align-middle">Search</span>
              </a>
              <b-input-group
                v-if="showSearchBar"
                class="input-group-merge mr-50"
              >
                <b-form-input
                  v-model="searchQuery"
                  placeholder="Search..."
                />
                <b-input-group-append is-text>
                  <feather-icon
                    icon="XIcon"
                    class="cursor-pointer"
                    @click="searchQuery = ''; showSearchBar = !showSearchBar"
                  />
                </b-input-group-append>
              </b-input-group>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refAllExpenseCategoryTable"
        class="position-relative event_list"
        :class="{ low_items: expenseCategoryData.length < 4 }"
        :items="fetchExpenseCategories"
        responsive
        :fields="tableColumns"
        primary-key="_id"
        :sort-by.sync="sortBy"
        show-empty
        no-sort-reset
        empty-text="No matching records found"
        :sort-desc.sync="isSortDirDesc"
      >
        <!-- Column: Role -->
        <template #cell(stringID)="data">
          <div>
            <span
              class="text-bold-black"
            >
              {{ data.item.stringID }}
            </span>
          </div>
        </template>

        <template #cell(createdAt)="data">
          <div class="text-nowrap">
            <span class="">{{ dateFormatWithTime(data.item.createdAt) }}</span>
          </div>
        </template>

        <template #cell(code)="data">
          <div class="text-nowrap">
            <span class="">{{ data.item.code || '-' }}</span>
          </div>
        </template>

        <template #cell(departments)="data">
          <div v-if="data.item.isAllDepartments">
            <span>All Departments</span>
          </div>
          <div v-else>
            <span
              v-for="(dept, deptIndex) in data.item.departments"
              :key="deptIndex"
            >
              {{ dept.name }}<span v-if="data.item.departments.length != deptIndex + 1">, </span>
            </span>
          </div>
        </template>

        <!-- Column: Status -->
        <template #cell(status)="data">
          <b-badge
            :variant="`light-${resolveExpenseCategoryStatusVariant(data.item.status)}`"
            class="text-capitalize"
          >
            {{ data.item.status }}
          </b-badge>
        </template>

        <!-- Column: Actions -->
        <template
          v-if="canViewThisAction('update', 'ExpenseCategory') || canViewThisAction('delete', 'ExpenseCategory')"
          #cell(actions)="data"
        >
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="canViewThisAction('update', 'ExpenseCategory')"
              @click="openEditExpenseCategoryModal(data.item)"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">Edit</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="canViewThisAction('delete', 'ExpenseCategory')"
              @click="deleteEntity(data.item._id, data.item.name)"
            >
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50">Delete</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>

      </b-table>
    </b-card>

    <div
      v-if="totalExpenseCategories > perPage"
      class="mx-2 mb-2"
    >
      <b-row>
        <!-- Pagination -->
        <b-col
          cols="12"
          sm="12"
          class="d-flex align-items-center justify-content-center pagination-content"
        >

          <b-pagination
            v-model="currentPage"
            :total-rows="totalExpenseCategories"
            :per-page="perPage"
            first-number
            last-number
            class="mb-0 mt-1 mt-sm-0"
            align="center"
            prev-text="Prev"
            next-text="Next"
          >
            <template #prev-text>
              <feather-icon
                icon="ChevronLeftIcon"
                size="18"
              />
              <span>Prev</span>
            </template>
            <template #next-text>
              <span>Next</span>
              <feather-icon
                icon="ChevronRightIcon"
                size="18"
              />
            </template>
          </b-pagination>

        </b-col>

      </b-row>
    </div>

    <!-- create new expense category modal -->
    <b-modal
      id="create-expense-category-modal"
      ref="create-expense-category-modal"
      modal-class="status-update-modal"
      centered
      no-stacking
      no-close-on-backdrop
      header-class="front-modal"
      title="Create Expense Category"
      @cancel="closeCreateExpenseCategoryModal"
      @ok="submitCreateExpenseCategoryData"
    >
      <b-form @submit.prevent="submitCreateExpenseCategoryData">
        <validation-observer
          ref="allCreateExpenseCategoryForm"
        >
          <b-form-group
            label="Type*"
            label-for="expense-category-type"
          >
            <validation-provider
              #default="{ errors }"
              name="Expense Category Type"
              vid="type"
              rules="required"
            >
              <v-select
                id="expense-category-type"
                v-model="expenseCategoryType"
                label="title"
                placeholder="Select from list"
                :options="expenseCategoryTypeOptions"
                :clearable="false"
                @input="expenseCategoryTypeValidation == true ? expenseCategoryTypeValidation = false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
              <small
                v-if="expenseCategoryTypeValidation"
                class="text-danger"
              >
                {{ expenseCategoryTypeError }}
              </small>
            </validation-provider>
          </b-form-group>
          <b-form-group
            label="Name*"
            label-for="expense-category-name"
          >
            <validation-provider
              #default="{ errors }"
              name="Name"
              vid="name"
              rules="required"
            >
              <b-form-input
                id="expense-category-name"
                v-model="name"
                placeholder="e.g. Event & Functions, Advertisement, Payroll"
                :state="(errors.length > 0 || nameValidation) ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
              <small
                v-if="nameValidation"
                class="text-danger"
              >
                {{ nameError }}
              </small>
            </validation-provider>
          </b-form-group>
          <b-form-group
            label="Budget Code (Optional)"
            label-for="expense-category-finance-code"
          >
            <validation-provider
              #default="{ errors }"
              name="Budget Code"
              vid="financeCode"
              rules=""
            >
              <b-form-input
                id="expense-category-finance-code"
                v-model="financeCode"
                placeholder="e.g. 7015680000"
                :state="(errors.length > 0 || financeCodeValidation) ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
              <small
                v-if="financeCodeValidation"
                class="text-danger"
              >
                {{ financeCodeError }}
              </small>
            </validation-provider>
          </b-form-group>
          <validation-provider
            #default="{ errors }"
            name="Usable by Department"
            vid="department"
            rules="required"
          >
            <b-form-group
              label="Usable by Department*"
              label-for="expense-category-department"
              :state="(errors.length > 0 || departmentValidation) ? false : null"
            >
              <v-select
                id="expense-category-department"
                v-model="department"
                label="name"
                placeholder="Select from list"
                multiple
                :options="departmentOptions"
                :reduce="name => name._id"
                :clearable="true"
                @input="updateDepartmentData"
              />
              <small class="text-danger">{{ errors[0] }}</small>
              <small
                v-if="departmentValidation"
                class="text-danger"
              >
                {{ departmentError }}
              </small>
            </b-form-group>
          </validation-provider>
          <b-form-group
            label="Status*"
            label-for="expense-category-status"
          >
            <validation-provider
              #default="{ errors }"
              name="Status"
              vid="status"
              rules="required"
            >
              <v-select
                id="expense-category-status"
                v-model="status"
                label="title"
                :options="statusOptions"
                :reduce="title => title.code"
                :clearable="false"
                @input="statusValidation == true ? statusValidation = false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
              <small
                v-if="statusValidation"
                class="text-danger"
              >
                {{ statusError }}
              </small>
            </validation-provider>
          </b-form-group>
        </validation-observer>
      </b-form>
      <template #modal-footer="{ ok, cancel }">
        <b-button
          variant="flat-primary"
          @click="cancel()"
        >
          Cancel
        </b-button>
        <b-button
          variant="success"
          class="custom-icon"
          :disabled="buttonStatus"
          @click="ok()"
        >
          <feather-icon
            icon="PlusIcon"
            size="16"
            class="mr-50"
          />
          <span class="align-middle">Create</span>
        </b-button>
      </template>
    </b-modal>
    <!--  /. create new expense category modal -->
    <!-- edit expense category modal -->
    <b-modal
      id="edit-expense-category-modal"
      ref="edit-expense-category-modal"
      modal-class="status-update-modal"
      centered
      no-stacking
      no-close-on-backdrop
      header-class="front-modal"
      title="Edit Expense Category"
      @cancel="closeEditExpenseCategoryModal"
      @ok="submitEditExpenseCategoryData"
    >
      <b-form @submit.prevent="submitEditExpenseCategoryData">
        <validation-observer
          ref="allEditExpenseCategoryForm"
        >
          <b-form-group
            label="Type*"
            label-for="expense-category-type"
          >
            <validation-provider
              #default="{ errors }"
              name="Expense Category Type"
              vid="type"
              rules="required"
            >
              <v-select
                id="expense-category-type"
                v-model="expenseCategoryType"
                label="title"
                placeholder="Select from list"
                :options="expenseCategoryTypeOptions"
                :clearable="false"
                @input="expenseCategoryTypeValidation == true ? expenseCategoryTypeValidation = false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
              <small
                v-if="expenseCategoryTypeValidation"
                class="text-danger"
              >
                {{ expenseCategoryTypeError }}
              </small>
            </validation-provider>
          </b-form-group>
          <b-form-group
            label="Name*"
            label-for="expense-category-name"
          >
            <validation-provider
              #default="{ errors }"
              name="Name"
              vid="name"
              rules="required"
            >
              <b-form-input
                id="expense-category-name"
                v-model="name"
                placeholder="e.g. Event & Functions, Advertisement, Payroll"
                :state="(errors.length > 0 || nameValidation) ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
              <small
                v-if="nameValidation"
                class="text-danger"
              >
                {{ nameError }}
              </small>
            </validation-provider>
          </b-form-group>
          <b-form-group
            label="Budget Code (Optional)"
            label-for="expense-category-finance-code"
          >
            <validation-provider
              #default="{ errors }"
              name="Budget Code"
              vid="financeCode"
              rules=""
            >
              <b-form-input
                id="expense-category-finance-code"
                v-model="financeCode"
                placeholder="e.g. 7015680000"
                :state="(errors.length > 0 || financeCodeValidation) ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
              <small
                v-if="financeCodeValidation"
                class="text-danger"
              >
                {{ financeCodeError }}
              </small>
            </validation-provider>
          </b-form-group>
          <validation-provider
            #default="{ errors }"
            name="Usable by Department"
            vid="department"
            rules="required"
          >
            <b-form-group
              label="Usable by Department*"
              label-for="expense-category-department"
              :state="(errors.length > 0 || departmentValidation) ? false : null"
            >
              <v-select
                id="expense-category-department"
                v-model="department"
                label="name"
                placeholder="Select from list"
                multiple
                :options="departmentOptions"
                :reduce="name => name._id"
                :clearable="true"
                @input="updateDepartmentData"
              />
              <small class="text-danger">{{ errors[0] }}</small>
              <small
                v-if="departmentValidation"
                class="text-danger"
              >
                {{ departmentError }}
              </small>
            </b-form-group>
          </validation-provider>
          <b-form-group
            label="Status*"
            label-for="expense-category-status"
          >
            <validation-provider
              #default="{ errors }"
              name="Status"
              vid="status"
              rules="required"
            >
              <v-select
                id="expense-category-status"
                v-model="status"
                label="title"
                :options="statusOptions"
                :reduce="title => title.code"
                :clearable="false"
                @input="statusValidation == true ? statusValidation = false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
              <small
                v-if="statusValidation"
                class="text-danger"
              >
                {{ statusError }}
              </small>
            </validation-provider>
          </b-form-group>
        </validation-observer>
      </b-form>
      <template #modal-footer="{ ok, cancel }">
        <b-button
          variant="flat-primary"
          @click="cancel()"
        >
          Cancel
        </b-button>
        <b-button
          variant="success"
          class="custom-icon"
          :disabled="buttonStatus"
          @click="ok()"
        >
          <feather-icon
            icon="SaveIcon"
            size="16"
            class="mr-50"
          />
          <span class="align-middle">Save Changes</span>
        </b-button>
      </template>
    </b-modal>
    <!--  /. edit expense category modal -->
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BCard, BRow, BCol, BFormInput, BTable, BInputGroup, BInputGroupAppend,
  BBadge, BDropdown, BDropdownItem, BPagination, BForm, BFormGroup, BButton,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import { ref, onUnmounted } from '@vue/composition-api'
import { avatarText } from '@core/utils/filter'
import { required } from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { useUtils as useAclUtils } from '@/libs/acl/custom'

import store from '@/store'

import useAllExpenseCategoryList from './useAllExpenseCategoryList'
import expenseCategoryStoreModule from './expenseCategoryStoreModule'

import ToastificationContentCustom from '../ToastificationContentCustom.vue'

export default {
  components: {
    BButton,
    BForm,
    BFormGroup,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BTable,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BInputGroup,
    BInputGroupAppend,

    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
    vSelect,

    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      buttonStatus: false,
      expenseCategoryType: '',
      expenseCategoryTypeValidation: false,
      expenseCategoryTypeError: 'Valid Expense Category Type is required.',
      name: '',
      nameValidation: false,
      nameError: 'Valid name is required',
      financeCode: '',
      financeCodeValidation: false,
      financeCodeError: 'Valid Budget Code is required.',
      department: ['all'],
      departmentValidation: false,
      departmentError: 'Valid Department is required',
      departmentOptions: [
        {
          name: 'All Departments',
          _id: 'all',
        },
      ],
      onlyDepartmentOptions: [],
      status: 'active',
      statusValidation: false,
      statusError: 'Valid status is required',
      statusOptions: [
        { title: 'Active', code: 'active' },
        { title: 'View Only', code: 'view only' },
      ],
      selectedExpenseCategoryType: 'Operating Expenses',
      expenseCategoryTypeOptions: [
        'Operating Expenses',
        'CAPEX (Fixed Assets)',
        'CAPEX (Lease Assets)',
        'Projects',
        'Non-budgeted',
      ],
      expenseCategoryEditId: '',
      required,
    }
  },

  beforeMount() {
    this.$http.get('inventory/departments/respond-with/name-and-id')
      .then(response => {
        this.onlyDepartmentOptions = response.data.departments
        this.departmentOptions = this.departmentOptions.concat(response.data.departments ?? [])
      })
      .catch(error => {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  },

  methods: {
    updateDepartmentData() {
      this.departmentValidation = false
      if (this.department.includes('all')) {
        this.department = this.department.filter(el => el === 'all')
      }
    },
    expenseCategoryTypeFiltering(eCategoryType) {
      if (eCategoryType) {
        this.expenseCategoryTypeFilter = eCategoryType
        this.selectedExpenseCategoryType = eCategoryType
      } else {
        this.expenseCategoryTypeFilter = this.selectedExpenseCategory
      }
    },
    openCreateExpenseCategoryModal() {
      this.expenseCategoryType = this.$route.query.categoryType ? this.$route.query.categoryType : this.selectedExpenseCategoryType
      this.name = ''
      this.financeCode = ''
      this.department = ['all']
      this.status = 'active'
      this.$root.$emit('bv::show::modal', 'create-expense-category-modal', '')
    },
    closeCreateExpenseCategoryModal() {
      this.$root.$emit('bv::hide::modal', 'create-expense-category-modal', '')
    },
    openEditExpenseCategoryModal(expenseCategory) {
      this.expenseCategoryType = expenseCategory.type ?? ''
      this.name = expenseCategory.name ?? ''
      this.financeCode = expenseCategory.code ?? ''
      this.department = expenseCategory.isAllDepartments ? ['all'] : expenseCategory.departments.map(a => a._id) ?? []
      this.status = expenseCategory.status ?? ''
      this.expenseCategoryEditId = expenseCategory._id
      this.$root.$emit('bv::show::modal', 'edit-expense-category-modal', '')
    },
    closeEditExpenseCategoryModal() {
      this.$root.$emit('bv::hide::modal', 'edit-expense-category-modal', '')
    },
    submitCreateExpenseCategoryData(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.allCreateExpenseCategoryForm.validate().then(success => {
        if (success) {
          store.commit('appConfig/UPDATE_LOADER', true)
          this.buttonStatus = true
          const formData = new FormData()
          if (this.department.includes('all')) {
            const departmentMapping = this.onlyDepartmentOptions.map(option => option._id)
            formData.append('departments', JSON.stringify(departmentMapping))
            formData.append('isAllDepartments', true)
          } else {
            formData.append('departments', JSON.stringify(this.department))
            formData.append('isAllDepartments', false)
          }
          formData.append('type', this.expenseCategoryType)
          formData.append('name', this.name)
          formData.append('code', this.financeCode)
          formData.append('status', this.status)
          this.$http.post('budget/expense-category/store', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
            .then(response => {
              this.refetchData()
              this.closeCreateExpenseCategoryModal()
              this.buttonStatus = false
              // clear formData
              this.expenseCategoryType = ''
              this.name = ''
              this.financeCode = ''
              this.department = ''
              this.status = 'active'
              this.expenseCategoryTypeFilter = response.data.data.type
              const toastMessage = `Expense Category <span class="text-bold-black">${response.data.data.name}</span> - ${response.data.data.code} has been created.`
              this.$toast({
                component: ToastificationContentCustom,
                props: {
                  title: 'Expense Category Created Successfully!',
                  text: toastMessage,
                  icon: 'CheckCircleIcon',
                  variant: 'success',
                },
              })
            })
            .catch(error => {
              if (error.data.errors) {
                this.buttonStatus = false
                error.data.errors.forEach(validationError => {
                  if (validationError.param === 'type') {
                    this.expenseCategoryTypeErrorError = validationError.msg
                    this.expenseCategoryTypeErrorValidation = true
                  }
                  if (validationError.param === 'name') {
                    this.nameError = validationError.msg
                    this.nameValidation = true
                  }
                  if (validationError.param === 'code') {
                    this.financeCodeError = validationError.msg
                    this.financeCodeValidation = true
                  }
                  if (validationError.param === 'departments') {
                    this.departmentError = validationError.msg
                    this.departmentValidation = true
                  }
                  if (validationError.param === 'status') {
                    this.statusError = validationError.msg
                    this.statusValidation = true
                  }
                })
              } else {
                this.buttonStatus = false
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
                    icon: 'AlertTriangleIcon',
                    variant: 'danger',
                  },
                })
              }
            })
        }
      })
    },
    submitEditExpenseCategoryData(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.allEditExpenseCategoryForm.validate().then(success => {
        if (success) {
          store.commit('appConfig/UPDATE_LOADER', true)
          this.buttonStatus = true
          const formData = new FormData()
          if (this.department.includes('all')) {
            const departmentMapping = this.onlyDepartmentOptions.map(option => option._id)
            formData.append('departments', JSON.stringify(departmentMapping))
            formData.append('isAllDepartments', true)
          } else {
            formData.append('departments', JSON.stringify(this.department))
            formData.append('isAllDepartments', false)
          }
          formData.append('type', this.expenseCategoryType)
          formData.append('name', this.name)
          formData.append('code', this.financeCode)
          formData.append('status', this.status)
          this.$http.patch(`budget/expense-category/${this.expenseCategoryEditId}/update`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
            .then(response => {
              this.refetchData()
              this.closeEditExpenseCategoryModal()
              this.buttonStatus = false
              this.expenseCategoryTypeFilter = response.data.data.type
              const toastMessage = `Expense Category <span class="text-bold-black">${response.data.data.name}</span> - ${response.data.data.code} has been updated successfully.`
              this.$toast({
                component: ToastificationContentCustom,
                props: {
                  title: 'Expense Category is edited!',
                  text: toastMessage,
                  icon: 'EditIcon',
                  variant: 'primary',
                },
              })
            })
            .catch(error => {
              if (error.data.errors) {
                this.buttonStatus = false
                error.data.errors.forEach(validationError => {
                  if (validationError.param === 'type') {
                    this.expenseCategoryTypeErrorError = validationError.msg
                    this.expenseCategoryTypeErrorValidation = true
                  }
                  if (validationError.param === 'name') {
                    this.nameError = validationError.msg
                    this.nameValidation = true
                  }
                  if (validationError.param === 'code') {
                    this.financeCodeError = validationError.msg
                    this.financeCodeValidation = true
                  }
                  if (validationError.param === 'departments') {
                    this.departmentError = validationError.msg
                    this.departmentValidation = true
                  }
                  if (validationError.param === 'status') {
                    this.statusError = validationError.msg
                    this.statusValidation = true
                  }
                })
              } else {
                this.buttonStatus = false
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
                    icon: 'AlertTriangleIcon',
                    variant: 'danger',
                  },
                })
              }
            })
        }
      })
    },
    deleteEntity(id, name) {
      this.$http.get(`budget/expense-category/${id}/check-existing-budget`)
        .then(response => {
          if (response.data.exists) {
            this.$swal({
              title: `${name}`,
              html: '<b>cannot be deleted</b> <br> <br> This expense category already in use in existing budgets and it is not possible to delete it. Do you want to change its status to “View only” instead? <br> <br> <b>All expenses under a “View only” expense category will no longer be available for allocation to Purchase Requests.</b> <br> <br> This action can be reversed later.',
              // eslint-disable-next-line global-require
              imageUrl: require('@/assets/images/icons/warning.png'),
              imageWidth: 80,
              imageHeight: 80,
              imageAlt: 'Custom Icon',
              reverseButtons: true,
              showCancelButton: true,
              confirmButtonText: 'Yes, change to “View only”',
              cancelButtonText: 'No, Go Back',
              customClass: {
                confirmButton: 'btn btn-primary ml-1 mt-1',
                cancelButton: 'btn btn-outline-primary mt-1',
              },
              buttonsStyling: false,
              width: 600,
            })
              .then(confirm => {
                if (confirm.value) {
                  const formData = new FormData()
                  formData.append('status', 'view only')
                  this.$http.patch(`budget/expense-category/${id}/freeze`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                  })
                    .then(() => {
                      this.refetchData()
                      const toastMessage = `${name} expense category status has been changed to View only`
                      this.$toast({
                        component: ToastificationContentCustom,
                        props: {
                          title: 'Expense Category status is edited!',
                          text: toastMessage,
                          icon: 'ArchiveIcon',
                          variant: 'warning',
                        },
                      })
                    })
                    .catch(error => {
                      if (error.data.errors) {
                        error.data.errors.forEach(validationError => {
                          if (validationError.param === 'status') {
                            this.statusError = validationError.msg
                            this.statusValidation = true
                          }
                        })
                      } else {
                        this.$toast({
                          component: ToastificationContent,
                          props: {
                            title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
                            icon: 'AlertTriangleIcon',
                            variant: 'danger',
                          },
                        })
                      }
                    })
                }
              })
          } else {
            this.$swal({
              title: 'Are you sure to delete',
              html: `<span style="font-weight: 600; font-size: 24px;">${name}?</span> <br> <br> Deleting the expense category cannot be undone and all the data will be lost. Related budgets may not be accessible too.`,
              // eslint-disable-next-line global-require
              imageUrl: require('@/assets/images/icons/warning.png'),
              imageWidth: 80,
              imageHeight: 80,
              imageAlt: 'Custom Icon',
              reverseButtons: true,
              showCancelButton: true,
              confirmButtonText: 'Yes, Delete!',
              cancelButtonText: 'No, Go Back',
              customClass: {
                confirmButton: 'btn btn-primary ml-1',
                cancelButton: 'btn btn-outline-primary',
              },
              buttonsStyling: false,
              width: 600,
            })
              .then(result => {
                if (result.value) {
                  this.toDeletedID = id
                  this.deletedExpenseCategoryName = name
                  this.deleteExpenseCategory()
                }
              })
          }
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
  },
  setup() {
    const showSearchBar = ref(false)
    const EXPENSE_CATEGORY_APP_STORE_MODULE_NAME = 'app-all-expense-categories'

    // Register module
    if (!store.hasModule(EXPENSE_CATEGORY_APP_STORE_MODULE_NAME)) store.registerModule(EXPENSE_CATEGORY_APP_STORE_MODULE_NAME, expenseCategoryStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(EXPENSE_CATEGORY_APP_STORE_MODULE_NAME)) store.unregisterModule(EXPENSE_CATEGORY_APP_STORE_MODULE_NAME)
    })

    const { canViewThisAction } = useAclUtils()

    const {
      fetchExpenseCategories,
      tableColumns,
      perPage,
      currentPage,
      totalExpenseCategories,
      expenseCategoryData,
      dataMeta,
      perPageOptions,
      searchQuery,
      expenseCategoryTypeFilter,
      sortBy,
      isSortDirDesc,
      refAllExpenseCategoryTable,
      refetchData,

      toDeletedID,
      deletedExpenseCategoryName,
      deleteExpenseCategory,

      // UI
      resolveExpenseCategoryStatusVariant,

      // Extra Filters
    } = useAllExpenseCategoryList()

    return {

      // Sidebar

      fetchExpenseCategories,
      tableColumns,
      perPage,
      currentPage,
      totalExpenseCategories,
      dataMeta,
      perPageOptions,
      searchQuery,
      expenseCategoryTypeFilter,
      sortBy,
      isSortDirDesc,
      refAllExpenseCategoryTable,
      refetchData,
      showSearchBar,
      toDeletedID,
      deletedExpenseCategoryName,
      deleteExpenseCategory,
      expenseCategoryData,

      // Filter
      avatarText,
      canViewThisAction,

      // UI
      resolveExpenseCategoryStatusVariant,
    }
  },
}
</script>

<style lang="scss" scoped>
.modal .form-control {
     margin: 0;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
